import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { OrkevaMark } from "@/components/brand/OrkevaMark";

/**
 * Premium loading state: animated ORKEVA mark + progress bar + rotating status.
 * `stages` is an array of status strings shown in sequence.
 */
export function OrkevaLoader({
  stages = ["Analyzing…", "Comparing…", "Evaluating…", "Generating recommendation…", "Building confidence score…"],
  title = "ORKEVA is thinking",
  subtitle = "Running a full multi-agent reasoning pass. Usually ~30–45 seconds.",
  intervalMs = 4000,
  testId = "orkeva-loader",
}) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => Math.min(i + 1, stages.length - 1)), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs, stages.length]);

  const progress = Math.round(((idx + 1) / stages.length) * 100);

  return (
    <div
      className="card-crisp flex flex-col items-center px-8 py-14 text-center"
      data-testid={testId}
    >
      <div className="relative flex h-20 w-20 items-center justify-center">
        <span className="absolute inset-0 rounded-full border border-primary/20 animate-spin-slow" />
        <OrkevaMark size={44} className="text-primary animate-pulse-soft" />
      </div>

      <h2 className="mt-6 font-display text-xl font-bold tracking-tight">{title}</h2>
      <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">{subtitle}</p>

      <div className="mt-8 w-full max-w-xs">
        <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
          <motion.div
            className="h-full rounded-full bg-primary"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />
        </div>
        <div className="mt-4 h-5 overflow-hidden">
          <motion.p
            key={idx}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="font-mono text-sm font-medium text-foreground"
            data-testid="loader-status"
          >
            {stages[idx]}
          </motion.p>
        </div>
      </div>
    </div>
  );
}
